import { memo, useCallback, useState } from 'react';
import { MenuIcon, DeleteIcon } from '@/components/ui/Icons';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import { ContextMenuDivider } from '@/components/ui/ContextMenu/ContextMenuDivider';
import type { Reader } from '@/api/client';
import { ReaderCatalogReaderTagsMenuCascadeRow } from '@/components/ReadersTable/ReaderCatalogReaderTagsMenuCascadeRow';
import '@/components/ProjectsTable/EditableCell.css';

interface ReadersTableRowMenuProps {
  reader: Reader;
  disabled?: boolean;
  onRequestDelete: (reader: Reader) => void;
  /** Каталог: открыть «Управление каталогом» на этой персоне */
  onOpenPersonManage?: (personId: string) => void;
  /** Модалка справочника тегов (с пунктом меню «Теги чтеца» доступны назначение и эта модалка) */
  onOpenTagsCatalogModal?: () => void;
}

export const ReadersTableRowMenu = memo(function ReadersTableRowMenu({
  reader,
  disabled,
  onRequestDelete,
  onOpenPersonManage,
  onOpenTagsCatalogModal,
}: ReadersTableRowMenuProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

  const handleButtonClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;
      setMenuAnchorEl(e.currentTarget);
      setMenuOpen((o) => !o);
    },
    [disabled]
  );

  const handleClose = useCallback(() => {
    setMenuOpen(false);
    setMenuAnchorEl(null);
  }, []);

  const handleDelete = useCallback(() => {
    onRequestDelete(reader);
    handleClose();
  }, [handleClose, reader, onRequestDelete]);

  const handleManagePerson = useCallback(() => {
    onOpenPersonManage?.(reader.id);
    handleClose();
  }, [handleClose, reader.id, onOpenPersonManage]);

  return (
    <>
      <div className="projects-table__actions readers-table-catalog__actions">
        <button
          type="button"
          className="projects-table__action-btn projects-table__actions-btn"
          onClick={handleButtonClick}
          disabled={disabled}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label="Меню"
        >
          <MenuIcon size={20} />
        </button>
      </div>
      <ContextMenu
        isOpen={menuOpen}
        onClose={handleClose}
        anchorPosition={null}
        anchorEl={menuAnchorEl}
        alignY="center"
        anchorPriorityAxis="y"
        repositionOnScroll={false}
      >
        <ul className="context-menu__list" role="menu">
          {onOpenPersonManage ? (
            <li className="context-menu__list-item">
              <button
                type="button"
                className="context-menu__item"
                disabled={!!disabled}
                role="menuitem"
                onClick={handleManagePerson}
              >
                <span className="context-menu__iconCell" aria-hidden>
                  <i className="fa-solid fa-id-card editable-cell__context-menu-fa" />
                </span>
                <span className="context-menu__item-text">Редактировать персону</span>
              </button>
            </li>
          ) : null}
          {onOpenTagsCatalogModal ? (
            <ReaderCatalogReaderTagsMenuCascadeRow
              reader={reader}
              disabled={!!disabled}
              onCloseRootMenu={handleClose}
              onOpenCatalogModal={onOpenTagsCatalogModal}
            />
          ) : null}
          {onOpenPersonManage || onOpenTagsCatalogModal ? <ContextMenuDivider inList listSpacing /> : null}
          <li className="context-menu__list-item">
            <button
              type="button"
              className="context-menu__item context-menu__item_danger"
              disabled={!!disabled}
              role="menuitem"
              onClick={handleDelete}
            >
              <span className="context-menu__iconCell" aria-hidden>
                <DeleteIcon size={18} />
              </span>
              <span className="context-menu__item-text">Удалить</span>
            </button>
          </li>
        </ul>
      </ContextMenu>
    </>
  );
});
